import { type InputHTMLAttributes, useEffect } from "react"

import { CountryEnum } from "@boundless/bl-api-client/customer/models"
import { useMask } from "@react-input/mask"
import {
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  useFormContext,
} from "react-hook-form"

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"

import type { MaskOptions } from "@react-input/mask/types"

const commonMaskOptions: MaskOptions = {
  track: ({ data, inputType }) =>
    inputType === "insert" ? data.toUpperCase() : data,
  replacement: { _: /\d/ },
}

const ssnMaskMappingOptions: Record<
  keyof typeof CountryEnum,
  Partial<MaskOptions>
> = {
  US: {
    ...commonMaskOptions,
    mask: "___-__-____",
  },
  CA: {
    ...commonMaskOptions,
    mask: "___-___-___",
  },
  GB: {
    ...commonMaskOptions,
    mask: "@@ ______ !",
    replacement: { "@": /[A-Z]/, _: /\d/, "!": /[A-D]/ },
  },
  AU: {
    ...commonMaskOptions,
    mask: "___ ___ ___",
  },
}

export type FormInputSSNProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>
  label?: string
  countryField: FieldPath<T>
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "name" | "label" | "value" | "onChange" | "onBlur" | "ref" | "defaultValue"
>

export function FormInputSSN<T extends FieldValues = FieldValues>({
  name,
  label,
  countryField,
  className,
  ...props
}: FormInputSSNProps<T>) {
  const form = useFormContext<T>()
  const country = form.watch(countryField)

  // Reset the SSN value when the country changes
  useEffect(() => {
    const countryState = form.getFieldState(countryField)

    if (countryState.isDirty) {
      // Country different from its default value; clear SSN field
      form.setValue(name, "" as FieldPathValue<T, typeof name>)
    } else {
      // Country is the default value; reset the SSN to its default value
      form.resetField(name)
    }
  }, [country])

  const ssnMaskOptions =
    country in ssnMaskMappingOptions
      ? ssnMaskMappingOptions[country]
      : undefined
  const inputRef = useMask(ssnMaskOptions ?? {})

  // Show the placeholder as the mask with all non-structure characters replaced by "_"
  const placeholder = ssnMaskOptions?.mask?.replace(/[^\s-]/g, "_")

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="w-full">
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <Input
              placeholder={placeholder}
              {...field}
              {...props}
              className={className}
              ref={inputRef}
            />
          </FormControl>
          <FormMessage className="form-error" />
        </FormItem>
      )}
    />
  )
}
